import type { ReactNode } from 'react';
import { cn } from './cn';

export type BadgeTone = 'neutral' | 'info' | 'ok' | 'warn' | 'danger';

// Light-theme status chips: a soft tinted surface, a readable colored label, and
// a faint same-hue ring. Kept low-saturation so they read as quiet badges, not
// neon — used sparingly per the design direction.
const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-ink-850 text-slate-300 ring-ink-700',
  info: 'bg-brand-50 text-brand-600 ring-brand-100',
  ok: 'bg-ok/10 text-ok ring-ok/20',
  warn: 'bg-warn/10 text-warn ring-warn/20',
  danger: 'bg-danger/10 text-danger ring-danger/20',
};

interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
  /** Show a leading status dot. */
  dot?: boolean;
  className?: string;
}

/** Small status pill with a tone-coloured ring + optional dot. */
export function Badge({ children, tone = 'neutral', dot, className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        TONES[tone],
        className,
      )}
    >
      {dot ? <span className={cn('h-1.5 w-1.5 rounded-full', dotColor(tone))} /> : null}
      {children}
    </span>
  );
}

function dotColor(tone: BadgeTone): string {
  switch (tone) {
    case 'ok':
      return 'bg-ok';
    case 'warn':
      return 'bg-warn';
    case 'danger':
      return 'bg-danger';
    case 'info':
      return 'bg-brand-400';
    default:
      return 'bg-slate-400';
  }
}
